/* eslint-disable */
import { useMemo } from "react";
import { Link } from "react-router-dom";

/**
 * Session 36 · Milestone C — canonical avatar component.
 *
 * Renders a member's profile image OR their initials fallback. Never renders
 * a broken-image icon. When `clickable` is true (default) the avatar links
 * to the member's public profile page.
 *
 * Accepts either:
 *   <MemberAvatar user={userObject} />
 *
 * where `user` may be the modern shape ({id, display_name, profile_image_url})
 * OR the legacy shape ({user_id, name, picture, author_picture}) — both are
 * handled so we can migrate call-sites gradually.
 */
export default function MemberAvatar({
  user,
  size = 40,
  clickable = true,
  showStatus = false,
  className = "",
  onClick,
  style: styleOverride,
}) {
  const normalized = useMemo(() => {
    if (!user) return null;
    const id =
      user.id ||
      user.user_id ||
      user.author_id ||
      user.member_id ||
      null;
    const displayName =
      user.display_name ||
      user.name ||
      user.author_name ||
      user.full_name ||
      "";
    const profileImageUrl =
      user.profile_image_url ||
      user.picture ||
      user.author_picture ||
      user.avatar_url ||
      user.avatar ||
      user.profile_photo ||
      user.profile_picture ||
      "";
    const initials =
      user.initials ||
      user.author_initials ||
      _initialsFrom(displayName);
    return { id, displayName, profileImageUrl, initials };
  }, [user]);

  if (!normalized) return null;

  const px = Number(size) || 40;
  const fontSize = Math.max(10, Math.round(px * 0.36));
  const containerStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: px,
    height: px,
    minWidth: px,
    borderRadius: "50%",
    background: normalized.profileImageUrl
      ? "transparent"
      : "linear-gradient(135deg, var(--gold-light,#d4a84b) 0%, var(--gold,#b8913a) 100%)",
    color: "#1b2340",
    fontSize,
    fontWeight: 700,
    letterSpacing: 0.2,
    overflow: "hidden",
    border: "1px solid rgba(27,35,64,0.12)",
    userSelect: "none",
    flexShrink: 0,
    ...(styleOverride || {}),
  };

  const inner = normalized.profileImageUrl ? (
    <img
      src={normalized.profileImageUrl}
      alt={normalized.displayName || "Member"}
      loading="lazy"
      onError={(e) => {
        // Never show a broken-image icon — fall back to initials on error.
        e.currentTarget.style.display = "none";
        const parent = e.currentTarget.parentElement;
        if (parent) {
          parent.style.background =
            "linear-gradient(135deg, var(--gold-light,#d4a84b) 0%, var(--gold,#b8913a) 100%)";
          const span = document.createElement("span");
          span.textContent = normalized.initials || "M";
          parent.appendChild(span);
        }
      }}
      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
    />
  ) : (
    <span>{normalized.initials || "M"}</span>
  );

  const testId = `avatar-${normalized.id || "anon"}`;

  const wrapper = (extra) => (
    <span
      data-testid={testId}
      className={`m-avatar ${className}`}
      style={containerStyle}
      onClick={onClick}
      {...(extra || {})}
    >
      {inner}
    </span>
  );

  if (clickable && normalized.id && !onClick) {
    return (
      <Link
        to={`/community/members/${normalized.id}`}
        className={`m-avatar-link ${className}`}
        style={{ textDecoration: "none", lineHeight: 0 }}
        data-testid={`avatar-link-${normalized.id}`}
        aria-label={`View ${normalized.displayName || "member"}'s profile`}
      >
        {wrapper()}
      </Link>
    );
  }

  return wrapper();
}


function _initialsFrom(name) {
  if (!name) return "M";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "M";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
